import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  CheckCircle2,
  AlertCircle,
  ArrowLeftRight,
  Maximize2,
  Minimize2,
  Zap,
  CheckSquare,
  Square,
  Shield,
  Lightbulb,
  AlertTriangle,
  History,
  Search,
  X,
  ChevronDown,
  ChevronRight,
  ListChecks,
  EyeOff,
  Loader2,
} from 'lucide-react';
import { BankTransaction } from '../../types/database';
import { ExpectedAction, reconciliationService } from '../../lib/reconciliationService';
import { bankTransactionsService } from '../../lib/bankTransactionsService';
import {
  matchRulesService,
  BankMatchRule,
  generateMatchSuggestions,
  MatchSuggestion,
  MatchLevel,
} from '../../lib/matchRulesService';
import { formatDateSafe } from '../../lib/dateUtils';
import { MatchHistoryView } from './MatchHistoryView';

// ─────────────────────────────────────────────────────────────────────────────
// Level meta: colours, icons, labels
// ─────────────────────────────────────────────────────────────────────────────
const LEVEL_META: Record<
  MatchLevel,
  {
    badge: string;
    rowChecked: string;
    rowUnchecked: string;
    checkColor: string;
    icon: React.ReactNode;
    label: string;
    panelBg: string;
    panelText: string;
    panelBorder: string;
  }
> = {
  1: {
    badge:       'bg-green-100 text-green-800 border border-green-300',
    rowChecked:  'border-green-400 bg-green-50',
    rowUnchecked:'border-gray-200 bg-white hover:bg-green-50/30',
    checkColor:  'text-green-600',
    icon:        <Shield className="w-3 h-3" />,
    label:       'חוק מותאם',
    panelBg:     'bg-green-50',
    panelText:   'text-green-800',
    panelBorder: 'border-green-200',
  },
  2: {
    badge:       'bg-blue-100 text-blue-800 border border-blue-300',
    rowChecked:  'border-blue-400 bg-blue-50',
    rowUnchecked:'border-gray-200 bg-white hover:bg-blue-50/30',
    checkColor:  'text-blue-600',
    icon:        <Lightbulb className="w-3 h-3" />,
    label:       'התאמה חכמה',
    panelBg:     'bg-blue-50',
    panelText:   'text-blue-800',
    panelBorder: 'border-blue-200',
  },
  3: {
    badge:       'bg-amber-100 text-amber-800 border border-amber-300',
    rowChecked:  'border-amber-400 bg-amber-50',
    rowUnchecked:'border-gray-200 bg-white hover:bg-amber-50/30',
    checkColor:  'text-amber-600',
    icon:        <AlertTriangle className="w-3 h-3" />,
    label:       'סכום בלבד',
    panelBg:     'bg-amber-50',
    panelText:   'text-amber-800',
    panelBorder: 'border-amber-200',
  },
};

const LEVEL_SECTION_TITLE: Record<MatchLevel, string> = {
  1: 'רמה 1 — חוק מותאם אישית',
  2: 'רמה 2 — התאמה חכמה (סכום + תיאור / קטגוריה)',
  3: 'רמה 3 — סכום זהה בלבד (דרוש אישור ידני)',
};

// ─────────────────────────────────────────────────────────────────────────────
// Sort options
// ─────────────────────────────────────────────────────────────────────────────
type SortOption = 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc';

const SORT_LABELS: Record<SortOption, string> = {
  date_desc:   'תאריך — חדש→ישן',
  date_asc:    'תאריך — ישן→חדש',
  amount_desc: 'סכום — גבוה→נמוך',
  amount_asc:  'סכום — נמוך→גבוה',
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const fmtDate = (d: string) => formatDateSafe(d);

const fmtAmt = (n: number) =>
  new Intl.NumberFormat('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

function groupBy<T>(arr: T[], key: (item: T) => string): [string, T[]][] {
  const map = new Map<string, T[]>();
  for (const item of arr) {
    const k = key(item);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(item);
  }
  return Array.from(map.entries());
}

// ─────────────────────────────────────────────────────────────────────────────
// useDebounce — returns a value that lags the input by `delay` ms
// ─────────────────────────────────────────────────────────────────────────────
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ─────────────────────────────────────────────────────────────────────────────
// FilterBar — defined at MODULE level so React never remounts it on re-render
// ─────────────────────────────────────────────────────────────────────────────
interface FilterBarProps {
  searchVal: string;
  onSearch:  (v: string) => void;
  amountVal: string;
  onAmount:  (v: string) => void;
  sortVal:   SortOption;
  onSort:    (v: SortOption) => void;
  onClear:   () => void;
  active:    boolean;
}

function FilterBar({
  searchVal, onSearch,
  amountVal, onAmount,
  sortVal,   onSort,
  onClear,   active,
}: FilterBarProps) {
  // Keep a stable ref to the amount input so we can restore focus after debounce
  const amountRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
      {/* Text search */}
      <div className="relative flex-1 min-w-0">
        <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchVal}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="חיפוש תיאור..."
          className="w-full pr-6 pl-2 py-1 text-xs border border-gray-200 rounded bg-white
            focus:outline-none focus:ring-1 focus:ring-primary-400 placeholder-gray-300"
          dir="rtl"
        />
      </div>
      {/* Amount filter */}
      <input
        ref={amountRef}
        type="number"
        value={amountVal}
        onChange={(e) => onAmount(e.target.value)}
        placeholder="סכום"
        className="w-20 px-2 py-1 text-xs border border-gray-200 rounded bg-white
          focus:outline-none focus:ring-1 focus:ring-primary-400 placeholder-gray-300"
        dir="ltr"
      />
      {/* Sort */}
      <select
        value={sortVal}
        onChange={(e) => onSort(e.target.value as SortOption)}
        className="px-1 py-1 text-xs border border-gray-200 rounded bg-white
          focus:outline-none focus:ring-1 focus:ring-primary-400 cursor-pointer"
        dir="rtl"
      >
        {(Object.keys(SORT_LABELS) as SortOption[]).map((k) => (
          <option key={k} value={k}>{SORT_LABELS[k]}</option>
        ))}
      </select>
      {/* Clear */}
      {active && (
        <button
          type="button"
          onClick={onClear}
          title="נקה סינון"
          className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

type InnerTab = 'reconcile' | 'history';

interface BankReconciliationViewProps {
  companyId: string;
}

export function BankReconciliationView({ companyId }: BankReconciliationViewProps) {
  // ── Inner tab ──────────────────────────────────────────────────────────────
  const [innerTab, setInnerTab] = useState<InnerTab>('reconcile');

  // ── Data ───────────────────────────────────────────────────────────────────
  const [unmatchedTransactions, setUnmatchedTransactions] = useState<BankTransaction[]>([]);
  const [expectedActions, setExpectedActions]             = useState<ExpectedAction[]>([]);
  const [matchRules, setMatchRules]                       = useState<BankMatchRule[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState<string | null>(null);

  // ── Multi-select for manual matching ─────────────────────────────────────
  /** Set of selected bank transaction IDs (left pane) */
  const [selectedBankIds, setSelectedBankIds]     = useState<Set<string>>(new Set());
  /** Set of selected expected-action IDs (right pane) */
  const [selectedExpectedIds, setSelectedExpectedIds] = useState<Set<string>>(new Set());
  const [matching, setMatching] = useState(false);

  // ── Ignore (do-not-reconcile) ─────────────────────────────────────────────
  /** IDs currently in-flight for an ignore request (shows spinner) */
  const [ignoringIds, setIgnoringIds] = useState<Set<string>>(new Set());

  // ── Fullscreen ─────────────────────────────────────────────────────────────
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ── Suggestions panel ──────────────────────────────────────────────────────
  const [checkedSuggestions, setCheckedSuggestions] = useState<Set<string>>(new Set());
  const [approvingBatch, setApprovingBatch]         = useState(false);
  const [suggestionsOpen, setSuggestionsOpen]       = useState(true);
  /**
   * IDs of bank-tx rows whose suggestion has been manually rejected.
   * Rejected pairs vanish from the suggestions accordion but their underlying
   * transactions remain fully accessible in the manual-matching panes below.
   */
  const [rejectedSuggestionIds, setRejectedSuggestionIds] = useState<Set<string>>(new Set());

  // ── Bank pane raw filter state (updates instantly for smooth typing) ────────
  const [bankSearch, setBankSearch] = useState('');
  const [bankAmount, setBankAmount] = useState('');
  const [bankSort, setBankSort]     = useState<SortOption>('date_desc');
  const [collapsedBankDates, setCollapsedBankDates] = useState<Set<string>>(new Set());

  // ── Expected pane raw filter state ────────────────────────────────────────
  const [expSearch, setExpSearch] = useState('');
  const [expAmount, setExpAmount] = useState('');
  const [expSort, setExpSort]     = useState<SortOption>('date_desc');

  // ── Debounced values — only these drive the expensive useMemo filters ──────
  // Sort is NOT debounced (instant select change; no typing involved)
  const bankSearchD = useDebounce(bankSearch, 300);
  const bankAmountD = useDebounce(bankAmount, 300);
  const expSearchD  = useDebounce(expSearch,  300);
  const expAmountD  = useDebounce(expAmount,  300);

  // ── Load data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (companyId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [txResult, actResult, rulesResult] = await Promise.all([
        reconciliationService.getUnmatchedBankTransactions(companyId),
        reconciliationService.getPendingExpectedActions(companyId),
        matchRulesService.getMatchRules(companyId),
      ]);
      if (txResult.error)  { setError(txResult.error.message  || 'שגיאה בטעינת תנועות בנק');    return; }
      if (actResult.error) { setError(actResult.error.message || 'שגיאה בטעינת פעולות צפויות'); return; }

      setUnmatchedTransactions(txResult.data  || []);
      setExpectedActions(actResult.data || []);
      setMatchRules(rulesResult.data    || []);
      setCheckedSuggestions(new Set());
      setSelectedBankIds(new Set());
      setSelectedExpectedIds(new Set());
    } catch (err) {
      console.error('Load reconciliation data error:', err);
      setError('שגיאה בטעינת נתונים');
    } finally {
      setLoading(false);
    }
  };

  // ── Suggestions ────────────────────────────────────────────────────────────
  // Raw suggestions from the algorithm (may include rejected ones)
  const rawSuggestions: MatchSuggestion[] = useMemo(
    () => generateMatchSuggestions(unmatchedTransactions, expectedActions, matchRules),
    [unmatchedTransactions, expectedActions, matchRules]
  );

  // Filtered suggestions — excluded any pair the user has manually rejected
  const suggestions: MatchSuggestion[] = useMemo(
    () => rawSuggestions.filter((s) => !rejectedSuggestionIds.has(s.bankTx.id)),
    [rawSuggestions, rejectedSuggestionIds]
  );

  /** Remove a suggestion pair from the accordion without touching the underlying data */
  const rejectSuggestion = useCallback((bankTxId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // don't toggle the checkbox
    setRejectedSuggestionIds((prev) => new Set(prev).add(bankTxId));
    // Also uncheck if it was checked
    setCheckedSuggestions((prev) => {
      const next = new Set(prev);
      next.delete(bankTxId);
      return next;
    });
  }, []);

  // Track previous suggestion count so we can distinguish "fresh load" (0→N)
  // from "row removed" (N→N-1). Only auto-open on the former.
  const prevSuggestionCountRef = useRef(0);

  useEffect(() => {
    const prev = prevSuggestionCountRef.current;
    const curr = rawSuggestions.length;
    prevSuggestionCountRef.current = curr;

    // When fresh data loads, reset all rejections and re-check the good ones
    const defaultChecked = rawSuggestions.filter((s) => s.matchLevel <= 2).map((s) => s.bankTx.id);
    setCheckedSuggestions(new Set(defaultChecked));
    setRejectedSuggestionIds(new Set()); // clear rejections on data reload

    // Auto-open ONLY when transitioning from zero → non-zero (i.e. fresh data).
    // Do NOT reopen if the user collapsed it and a row was merely removed (N→N-1).
    if (prev === 0 && curr > 0) setSuggestionsOpen(true);
  }, [rawSuggestions]);

  const toggleSuggestionsOpen = useCallback(() => setSuggestionsOpen((v) => !v), []);

  const suggestionsByLevel = useMemo(() => {
    const groups: Partial<Record<MatchLevel, MatchSuggestion[]>> = {};
    for (const s of suggestions) (groups[s.matchLevel] ??= []).push(s);
    return groups;
  }, [suggestions]);

  // ── Live sums for manual selection ────────────────────────────────────────
  const selectedBankSum = useMemo(() =>
    unmatchedTransactions
      .filter((tx) => selectedBankIds.has(tx.id))
      .reduce((acc, tx) => acc + tx.amount, 0),
    [unmatchedTransactions, selectedBankIds]
  );

  const selectedExpectedSum = useMemo(() =>
    expectedActions
      .filter((a) => selectedExpectedIds.has(a.id))
      .reduce((acc, a) => acc + a.amount, 0),
    [expectedActions, selectedExpectedIds]
  );

  /** True when at least one item is selected on each side (sums no longer need to match) */
  const canMatch = useMemo(() => {
    return selectedBankIds.size > 0 && selectedExpectedIds.size > 0;
  }, [selectedBankIds, selectedExpectedIds]);

  const sumDiff = useMemo(
    () => selectedBankSum - selectedExpectedSum,
    [selectedBankSum, selectedExpectedSum]
  );

  // ── Filtered + sorted bank transactions (uses debounced values) ──────────
  const filteredBankTx = useMemo(() => {
    const needle    = bankSearchD.trim().toLowerCase();
    const amtFilter = bankAmountD.trim() === '' ? null : parseFloat(bankAmountD.replace(/,/g, ''));

    let list = unmatchedTransactions.filter((tx) => {
      if (needle && !tx.description.toLowerCase().includes(needle)) return false;
      if (amtFilter !== null && !isNaN(amtFilter)) {
        if (Math.abs(Math.abs(tx.amount) - Math.abs(amtFilter)) > 0.01) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      switch (bankSort) {
        case 'date_asc':    return a.transaction_date.localeCompare(b.transaction_date);
        case 'date_desc':   return b.transaction_date.localeCompare(a.transaction_date);
        case 'amount_asc':  return a.amount - b.amount;
        case 'amount_desc': return b.amount - a.amount;
      }
    });

    return list;
  }, [unmatchedTransactions, bankSearchD, bankAmountD, bankSort]);

  const bankTxByDate = useMemo(
    () => groupBy(filteredBankTx, (tx) => tx.transaction_date),
    [filteredBankTx]
  );

  // ── Filtered + sorted expected actions (uses debounced values) ──────────
  const filteredExpected = useMemo(() => {
    const needle    = expSearchD.trim().toLowerCase();
    const amtFilter = expAmountD.trim() === '' ? null : parseFloat(expAmountD.replace(/,/g, ''));

    let list = expectedActions.filter((a) => {
      if (needle && !a.description.toLowerCase().includes(needle)) return false;
      if (amtFilter !== null && !isNaN(amtFilter)) {
        if (Math.abs(Math.abs(a.amount) - Math.abs(amtFilter)) > 0.01) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      switch (expSort) {
        case 'date_asc':    return a.action_date.localeCompare(b.action_date);
        case 'date_desc':   return b.action_date.localeCompare(a.action_date);
        case 'amount_asc':  return a.amount - b.amount;
        case 'amount_desc': return b.amount - a.amount;
      }
    });

    return list;
  }, [expectedActions, expSearchD, expAmountD, expSort]);

  // ── Manual match ───────────────────────────────────────────────────────────
  const handleMatch = async () => {
    if (!canMatch) return;
    setMatching(true); setError(null); setSuccess(null);

    const bankIds  = Array.from(selectedBankIds);
    const selected = expectedActions.filter((a) => selectedExpectedIds.has(a.id));

    try {
      const { error: matchError } = await reconciliationService.matchManyToMany(bankIds, selected);
      if (matchError) { setError(matchError.message || 'שגיאה בהתאמת פעולות'); return; }

      const bankCount = bankIds.length;
      const expCount  = selected.length;
      setSuccess(
        bankCount === 1 && expCount === 1
          ? 'פעולות הותאמו בהצלחה'
          : `${bankCount} תנועות בנק ↔ ${expCount} פעולות צפויות הותאמו בהצלחה`,
      );
      setSelectedBankIds(new Set());
      setSelectedExpectedIds(new Set());
      await loadData();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Match error:', err);
      setError('שגיאה בהתאמת פעולות');
    } finally {
      setMatching(false);
    }
  };

  // ── Batch approve suggestions ─────────────────────────────────────────────
  const handleApproveSuggestions = async () => {
    const toApprove = suggestions.filter((s) => checkedSuggestions.has(s.bankTx.id));
    if (!toApprove.length) return;
    setApprovingBatch(true); setError(null); setSuccess(null);
    let successCount = 0;
    const errors: string[] = [];
    for (const s of toApprove) {
      const { error: matchError } = await reconciliationService.matchTransactions(s.bankTx.id, s.expectedAction);
      if (matchError) errors.push(`${s.bankTx.description}: ${matchError.message}`);
      else successCount++;
    }
    if (errors.length) setError(`${errors.length} התאמות נכשלו: ${errors.join('; ')}`);
    if (successCount > 0) { setSuccess(`${successCount} התאמות אושרו בהצלחה`); setTimeout(() => setSuccess(null), 4000); }
    await loadData();
    setApprovingBatch(false);
  };

  // ── Selection toggles ─────────────────────────────────────────────────────
  const toggleBankTx = useCallback((id: string) => {
    setSelectedBankIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleExpected = useCallback((id: string) => {
    setSelectedExpectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleSuggestion = (bankTxId: string) => {
    setCheckedSuggestions((prev) => {
      const next = new Set(prev);
      next.has(bankTxId) ? next.delete(bankTxId) : next.add(bankTxId);
      return next;
    });
  };

  const toggleAllSuggestions = () => {
    setCheckedSuggestions(
      checkedSuggestions.size === suggestions.length
        ? new Set()
        : new Set(suggestions.map((s) => s.bankTx.id))
    );
  };

  const toggleBankDateGroup = useCallback((date: string) => {
    setCollapsedBankDates((prev) => {
      const next = new Set(prev);
      next.has(date) ? next.delete(date) : next.add(date);
      return next;
    });
  }, []);

  // ── Clear filters ─────────────────────────────────────────────────────────
  // ── Bulk-ignore handler ────────────────────────────────────────────────────
  const [bulkIgnoring, setBulkIgnoring] = useState(false);

  const handleBulkIgnore = useCallback(async () => {
    const ids = Array.from(selectedBankIds);
    if (ids.length === 0) return;

    setBulkIgnoring(true);
    setError(null);

    // Snapshot the transactions we are about to remove (for rollback)
    const toRemove = unmatchedTransactions.filter((t) => ids.includes(t.id));

    // Optimistic: strip them from local state immediately
    setUnmatchedTransactions((prev) => prev.filter((t) => !ids.includes(t.id)));
    setSelectedBankIds(new Set());

    const { count, error: bulkErr } = await bankTransactionsService.bulkIgnoreTransactions(ids);

    setBulkIgnoring(false);

    if (bulkErr) {
      // Full rollback
      setUnmatchedTransactions((prev) =>
        [...toRemove, ...prev].sort((a, b) =>
          b.transaction_date.localeCompare(a.transaction_date),
        ),
      );
      const msg =
        typeof bulkErr === 'object' && bulkErr !== null && 'message' in bulkErr
          ? (bulkErr as { message: string }).message
          : 'שגיאה בסימון עסקאות כ"לא להתאמה"';
      setError(msg);
      return;
    }

    setSuccess(`${count} עסקאות סומנו כ"לא להתאמה" בהצלחה`);
    setTimeout(() => setSuccess(null), 4000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBankIds, unmatchedTransactions]);

  // ── Ignore handler — optimistic removal, then DB sync ───────────────────
  const handleIgnoreTx = useCallback(async (tx: BankTransaction, e: React.MouseEvent) => {
    e.stopPropagation(); // don't toggle the checkbox on the parent row

    // Mark as in-flight
    setIgnoringIds((prev) => new Set(prev).add(tx.id));

    // Optimistic: remove from local state immediately
    setUnmatchedTransactions((prev) => prev.filter((t) => t.id !== tx.id));
    // Also deselect if it was selected
    setSelectedBankIds((prev) => {
      const next = new Set(prev);
      next.delete(tx.id);
      return next;
    });

    // Persist to DB  (status: 'pending' → 'ignored')
    const { error: ignoreErr } = await bankTransactionsService.toggleTransactionStatus(
      tx.id,
      'pending',
    );

    setIgnoringIds((prev) => {
      const next = new Set(prev);
      next.delete(tx.id);
      return next;
    });

    if (ignoreErr) {
      // Roll back: put the transaction back and show error
      setUnmatchedTransactions((prev) =>
        [tx, ...prev].sort((a, b) =>
          b.transaction_date.localeCompare(a.transaction_date),
        ),
      );
      const msg =
        typeof ignoreErr === 'object' && ignoreErr !== null && 'message' in ignoreErr
          ? (ignoreErr as { message: string }).message
          : 'שגיאה בסימון כ"לא להתאמה"';
      setError(msg);
    }
  }, []);

  const clearBankFilters = () => { setBankSearch(''); setBankAmount(''); setBankSort('date_desc'); };
  const clearExpFilters  = () => { setExpSearch('');  setExpAmount('');  setExpSort('date_desc'); };

  const bankFiltersActive = bankSearch !== '' || bankAmount !== '' || bankSort !== 'date_desc';
  const expFiltersActive  = expSearch  !== '' || expAmount  !== '' || expSort  !== 'date_desc';

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-600">טוען נתונים...</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      className={`flex flex-col overflow-hidden ${
        isFullscreen ? 'fixed inset-0 z-50 bg-gray-50 p-4' : 'h-[calc(100vh-140px)]'
      }`}
    >
      {/* ── Inner tab switcher ─────────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-b border-gray-200 mb-4">
        <nav className="-mb-px flex gap-6" aria-label="Reconciliation inner tabs">
          <button
            onClick={() => setInnerTab('reconcile')}
            className={`inline-flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium
              transition-colors duration-150
              ${innerTab === 'reconcile'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <ArrowLeftRight className="w-4 h-4" />
            התאמה ידנית
          </button>
          <button
            onClick={() => setInnerTab('history')}
            className={`inline-flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium
              transition-colors duration-150
              ${innerTab === 'history'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <History className="w-4 h-4" />
            היסטוריית התאמות
          </button>
        </nav>
      </div>

      {/* ── History tab ────────────────────────────────────────────────────── */}
      {innerTab === 'history' && (
        <div className="flex-1 overflow-y-auto">
          <MatchHistoryView companyId={companyId} />
        </div>
      )}

      {/* ── Reconcile tab ──────────────────────────────────────────────────── */}
      {innerTab === 'reconcile' && (
        <>
          {/* ── Top bar ──────────────────────────────────────────────────── */}
          <div className="flex-shrink-0">
            {/* Fullscreen toggle */}
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md shadow-sm
                  text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                title={isFullscreen ? 'יציאה ממסך מלא' : 'מסך מלא'}
              >
                {isFullscreen
                  ? <><Minimize2 className="w-3.5 h-3.5" /><span>יציאה ממסך מלא</span></>
                  : <><Maximize2 className="w-3.5 h-3.5" /><span>מסך מלא</span></>}
              </button>
            </div>

            {/* Banners */}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-md flex items-center gap-2 mb-2 text-sm">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" /><span>{success}</span>
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-md flex items-center gap-2 mb-2 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
              </div>
            )}

            {/* ── Suggested Matches — collapsible accordion ─────────────── */}
            {suggestions.length > 0 && (
              <div className="border border-gray-200 rounded-lg mb-2 overflow-hidden">
                <button
                  type="button"
                  onClick={toggleSuggestionsOpen}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200
                    hover:bg-gray-100 transition-colors text-right"
                  aria-expanded={suggestionsOpen}
                >
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary-600 flex-shrink-0" />
                    <span className="font-semibold text-gray-800 text-xs">
                      התאמות מוצעות ({suggestions.length})
                    </span>
                    {!suggestionsOpen && (
                      <span className="hidden sm:inline-flex items-center gap-1 mr-1">
                        {([1, 2, 3] as MatchLevel[]).map((lvl) => {
                          const m = LEVEL_META[lvl];
                          const count = suggestionsByLevel[lvl]?.length ?? 0;
                          if (!count) return null;
                          return (
                            <span key={lvl} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium ${m.badge}`}>
                              {m.icon}{count}
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {suggestionsOpen && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleAllSuggestions(); }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-600
                            hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
                        >
                          {checkedSuggestions.size === suggestions.length
                            ? <CheckSquare className="w-3.5 h-3.5" />
                            : <Square className="w-3.5 h-3.5" />}
                          {checkedSuggestions.size === suggestions.length ? 'בטל הכל' : 'בחר הכל'}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleApproveSuggestions(); }}
                          disabled={approvingBatch || checkedSuggestions.size === 0}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-600 text-white text-xs
                            font-medium rounded hover:bg-primary-700 disabled:opacity-50
                            disabled:cursor-not-allowed transition-colors"
                        >
                          {approvingBatch
                            ? <span className="animate-spin inline-block w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full" />
                            : <CheckCircle2 className="w-3.5 h-3.5" />}
                          אשר נבחרות ({checkedSuggestions.size})
                        </button>
                      </>
                    )}
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${suggestionsOpen ? 'rotate-180' : 'rotate-0'}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {suggestionsOpen && (
                  <div className="divide-y divide-gray-100 max-h-[35vh] overflow-y-auto">
                    {([1, 2, 3] as MatchLevel[]).map((lvl) => {
                      const group = suggestionsByLevel[lvl];
                      if (!group?.length) return null;
                      const m = LEVEL_META[lvl];
                      return (
                        <div key={lvl}>
                          <div className={`flex items-center gap-1.5 px-3 py-1 ${m.panelBg} ${m.panelText} border-b ${m.panelBorder}`}>
                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold ${m.badge}`}>
                              {m.icon}{m.label}
                            </span>
                            <span className="text-xs font-medium">{LEVEL_SECTION_TITLE[lvl]}</span>
                            {lvl === 3 && <span className="text-xs opacity-75 mr-auto">— בדוק לפני אישור</span>}
                          </div>
                          {group.map((s) => {
                            const checked = checkedSuggestions.has(s.bankTx.id);
                            return (
                              <div
                                key={s.bankTx.id}
                                onClick={() => toggleSuggestion(s.bankTx.id)}
                                className={`cursor-pointer flex items-center gap-2 px-3 py-1.5
                                  transition-colors border-b border-gray-50 last:border-0
                                  ${checked ? m.rowChecked : m.rowUnchecked}`}
                              >
                                <div className={`flex-shrink-0 ${checked ? m.checkColor : 'text-gray-300'}`}>
                                  {checked ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline gap-1.5">
                                    <span className="text-xs font-medium text-gray-800 truncate">{s.bankTx.description}</span>
                                    <span className={`text-xs flex-shrink-0 ${s.bankTx.amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                      {fmtAmt(s.bankTx.amount)} ₪
                                    </span>
                                  </div>
                                  <span className="text-xs text-gray-400">{fmtDate(s.bankTx.transaction_date)}</span>
                                </div>
                                <div className="flex flex-col items-center gap-0 flex-shrink-0 px-1">
                                  <ArrowLeftRight className="w-3.5 h-3.5 text-gray-400" />
                                  <span className="text-xs text-gray-400 text-center leading-tight max-w-[70px] truncate">{s.matchReason}</span>
                                </div>
                                <div className="flex-1 min-w-0 text-right">
                                  <div className="flex items-baseline justify-end gap-1.5">
                                    <span className={`text-xs flex-shrink-0 ${s.expectedAction.amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                      {fmtAmt(s.expectedAction.amount)} ₪
                                    </span>
                                    <span className="text-xs font-medium text-gray-800 truncate">{s.expectedAction.description}</span>
                                  </div>
                                  <span className="text-xs text-gray-400 block text-right">
                                    {fmtDate(s.expectedAction.action_date)}{' '}·{' '}
                                    <span className={`inline-flex items-center px-1 rounded text-xs font-medium ${
                                      s.expectedAction.type === 'recurring' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {s.expectedAction.type === 'recurring' ? 'קבועה' : 'ידועה'}
                                    </span>
                                  </span>
                                </div>

                                {/* ── Reject suggestion button ── */}
                                <button
                                  type="button"
                                  onClick={(e) => rejectSuggestion(s.bankTx.id, e)}
                                  title="דחה הצעה — הסר מהרשימה ושמור את הפעולות לטיפול ידני"
                                  style={{ opacity: 1, visibility: 'visible' }}
                                  className="flex-shrink-0 relative z-10 p-1.5 rounded
                                    text-slate-500 hover:text-red-600 hover:bg-red-100
                                    transition-colors duration-150"
                                  aria-label="דחה הצעה"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Manual Match Toolbar ──────────────────────────────────── */}
            {/*
              Shows:
                • Sum of selected bank txs  (left pill)
                • Diff indicator            (centre)
                • Sum of selected expected  (right pill)
                • "התאם פעולות" button      (enabled only when sums equal)
            */}
            <div className="flex items-center justify-center gap-2 mb-2 flex-wrap">
              {/* Bank-side sum */}
              <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border
                ${selectedBankIds.size > 0
                  ? 'bg-blue-50 text-blue-800 border-blue-200'
                  : 'bg-gray-50 text-gray-400 border-gray-200'}`}
              >
                <ListChecks className="w-3 h-3" />
                {selectedBankIds.size > 0
                  ? <>{selectedBankIds.size} בנק · <span className={selectedBankSum >= 0 ? 'text-green-700' : 'text-red-700'}>{fmtAmt(selectedBankSum)} ₪</span></>
                  : <span>בחר תנועות בנק</span>
                }
              </div>

              {/* Match button (centre) */}
              <button
                onClick={handleMatch}
                disabled={!canMatch || matching}
                title={
                  !canMatch
                    ? 'בחר תנועות משני הצדדים'
                    : Math.abs(sumDiff) >= 0.01
                    ? `התאם פעולות (הפרש: ${fmtAmt(Math.abs(sumDiff))} ₪)`
                    : 'התאם פעולות'
                }
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md font-medium text-sm
                  transition-all duration-150
                  ${canMatch && !matching
                    ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
              >
                <ArrowLeftRight className="w-4 h-4" />
                {matching ? 'מתאים...' : 'התאם פעולות'}
              </button>

              {/* Expected-side sum */}
              <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border
                ${selectedExpectedIds.size > 0
                  ? 'bg-purple-50 text-purple-800 border-purple-200'
                  : 'bg-gray-50 text-gray-400 border-gray-200'}`}
              >
                {selectedExpectedIds.size > 0
                  ? <><span className={selectedExpectedSum >= 0 ? 'text-green-700' : 'text-red-700'}>{fmtAmt(selectedExpectedSum)} ₪</span> · {selectedExpectedIds.size} צפי</>
                  : <span>בחר פעולות צפויות</span>
                }
                <ListChecks className="w-3 h-3" />
              </div>
            </div>

            {/* Diff info — neutral indicator when both sides selected and sums differ */}
            {selectedBankIds.size > 0 && selectedExpectedIds.size > 0 && Math.abs(sumDiff) >= 0.01 && (
              <div className="flex justify-center mb-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
                  bg-gray-100 text-gray-600 border border-gray-300">
                  <AlertCircle className="w-3 h-3" />
                  הפרש: {fmtAmt(Math.abs(sumDiff))} ₪ — הצפי והבנק שונים, ניתן לאשר בכל זאת
                </span>
              </div>
            )}
          </div>

          {/* ── Two-pane manual reconciliation area ────────────────────── */}
          <div className="flex-1 min-h-0 w-full">
            <div className="grid grid-cols-2 gap-3 h-full">

              {/* ── LEFT: Unmatched Bank Transactions ──────────────────── */}
              <div className="flex flex-col h-full min-h-0">
                <div className="flex-shrink-0 px-3 py-2 bg-gray-50 rounded-t-lg border border-b-0 border-gray-200">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 flex-shrink-0">בפועל — תנועות בנק</h3>

                    <div className="flex items-center gap-2 min-w-0">
                      {/* Bulk-ignore button — visible only when ≥1 bank rows are selected */}
                      {selectedBankIds.size > 0 && (
                        <button
                          type="button"
                          onClick={handleBulkIgnore}
                          disabled={bulkIgnoring}
                          title='סמן נבחרים כ"לא להתאמה"'
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded
                            text-xs font-medium
                            bg-orange-100 text-orange-700 border border-orange-300
                            hover:bg-orange-200 hover:border-orange-400
                            disabled:opacity-50 disabled:cursor-not-allowed
                            transition-colors flex-shrink-0"
                        >
                          {bulkIgnoring
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <EyeOff className="w-3 h-3" />}
                          {bulkIgnoring ? 'מסמן...' : `התעלם מ-${selectedBankIds.size} נבחרים`}
                        </button>
                      )}

                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {filteredBankTx.length !== unmatchedTransactions.length
                          ? `${filteredBankTx.length} / ${unmatchedTransactions.length}`
                          : `${unmatchedTransactions.length}`} ממתינות
                        {selectedBankIds.size > 0 && (
                          <span className="mr-1 text-blue-600 font-medium">· {selectedBankIds.size} נבחרו</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <FilterBar
                  searchVal={bankSearch} onSearch={setBankSearch}
                  amountVal={bankAmount} onAmount={setBankAmount}
                  sortVal={bankSort}     onSort={setBankSort}
                  onClear={clearBankFilters} active={bankFiltersActive}
                />

                <div className="flex-1 overflow-y-auto border border-t-0 border-gray-200 rounded-b-lg bg-white min-h-0">
                  {filteredBankTx.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      {unmatchedTransactions.length === 0
                        ? <p>אין תנועות בנק ממתינות להתאמה</p>
                        : <p>לא נמצאו תוצאות לפי הסינון הנוכחי</p>}
                    </div>
                  ) : (
                    <div>
                      {bankTxByDate.map(([date, txsInGroup]) => {
                        const isCollapsed = collapsedBankDates.has(date);
                        return (
                          <div key={date}>
                            {/* Date group header */}
                            <button
                              type="button"
                              onClick={() => toggleBankDateGroup(date)}
                              className="w-full flex items-center justify-between px-3 py-1 bg-gray-50 border-b border-gray-100
                                hover:bg-gray-100 transition-colors sticky top-0 z-10"
                            >
                              <div className="flex items-center gap-1.5">
                                {isCollapsed
                                  ? <ChevronRight className="w-3 h-3 text-gray-400" />
                                  : <ChevronDown  className="w-3 h-3 text-gray-400" />}
                                <span className="text-xs font-semibold text-gray-600">{fmtDate(date)}</span>
                                <span className="text-xs text-gray-400">({txsInGroup.length})</span>
                              </div>
                              <span className="text-xs text-gray-400">
                                {fmtAmt(txsInGroup.reduce((acc, tx) => acc + tx.amount, 0))} ₪
                              </span>
                            </button>

                            {!isCollapsed && txsInGroup.map((tx) => {
                              const isSelected = selectedBankIds.has(tx.id);
                              const isIgnoring = ignoringIds.has(tx.id);
                              return (
                                /*
                                 * Row layout:
                                 *   [selectable area — flex-1]  [ignore button — w-8]
                                 *
                                 * The outer <div> owns the selection background.
                                 * The inner <button> (flex-1) toggles the checkbox.
                                 * The ignore <button> (flex-shrink-0) is a sibling so
                                 * its click does NOT propagate to the select toggle.
                                 */
                                <div
                                  key={tx.id}
                                  className={`flex items-stretch border-b border-gray-100 last:border-0
                                    transition-colors group
                                    ${isSelected
                                      ? 'bg-blue-50 border-r-2 border-blue-500'
                                      : 'bg-white hover:bg-blue-50/60'}
                                    ${isIgnoring ? 'opacity-40 pointer-events-none' : ''}`}
                                >
                                  {/* ── Selectable area ─────────────────── */}
                                  <button
                                    type="button"
                                    onClick={() => toggleBankTx(tx.id)}
                                    className="flex-1 min-w-0 px-3 py-2 text-right"
                                  >
                                    <div className="flex justify-between items-center gap-2">
                                      <span className={`flex-shrink-0 ${isSelected ? 'text-blue-500' : 'text-gray-300'}`}>
                                        {isSelected
                                          ? <CheckSquare className="w-3.5 h-3.5" />
                                          : <Square className="w-3.5 h-3.5" />}
                                      </span>
                                      <span className="text-xs text-gray-400">{fmtDate(tx.transaction_date)}</span>
                                      <span className={`text-sm font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {fmtAmt(tx.amount)} ₪
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-900 font-medium truncate mt-0.5">{tx.description}</p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {tx.bank_accounts?.nickname || 'חשבון לא ידוע'}
                                      {tx.accounting_categories && (
                                        <span className="mr-1 text-gray-400">· {tx.accounting_categories.name}</span>
                                      )}
                                    </p>
                                  </button>

                                  {/* ── Ignore / do-not-reconcile button ── */}
                                  <button
                                    type="button"
                                    onClick={(e) => handleIgnoreTx(tx, e)}
                                    disabled={isIgnoring}
                                    title='סמן כ"לא להתאמה" — הסר מרשימת ההמתנה'
                                    className="flex-shrink-0 flex items-center justify-center
                                      w-8 border-r border-gray-100 rounded-r
                                      text-gray-300 hover:text-red-600 hover:bg-red-100
                                      opacity-0 group-hover:opacity-100
                                      focus-visible:opacity-100
                                      hover:scale-110
                                      transition-all duration-150
                                      disabled:cursor-not-allowed"
                                    aria-label="Do not reconcile"
                                  >
                                    {isIgnoring
                                      ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                                      : <EyeOff  className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* ── RIGHT: Expected Actions ─────────────────────────────── */}
              <div className="flex flex-col h-full min-h-0">
                <div className="flex-shrink-0 px-3 py-2 bg-gray-50 rounded-t-lg border border-b-0 border-gray-200">
                  <div className="flex items-baseline justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">צפי — פעולות צפויות</h3>
                    <span className="text-xs text-gray-500">
                      {filteredExpected.length !== expectedActions.length
                        ? `${filteredExpected.length} / ${expectedActions.length}`
                        : `${expectedActions.length}`} ממתינות
                      {selectedExpectedIds.size > 0 && (
                        <span className="mr-1 text-purple-600 font-medium">· {selectedExpectedIds.size} נבחרו</span>
                      )}
                    </span>
                  </div>
                </div>

                <FilterBar
                  searchVal={expSearch} onSearch={setExpSearch}
                  amountVal={expAmount} onAmount={setExpAmount}
                  sortVal={expSort}     onSort={setExpSort}
                  onClear={clearExpFilters} active={expFiltersActive}
                />

                <div className="flex-1 overflow-y-auto border border-t-0 border-gray-200 rounded-b-lg bg-white min-h-0">
                  {filteredExpected.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      {expectedActions.length === 0
                        ? <p>אין פעולות צפויות ממתינות</p>
                        : <p>לא נמצאו תוצאות לפי הסינון הנוכחי</p>}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {filteredExpected.map((action) => {
                        const isSelected = selectedExpectedIds.has(action.id);
                        return (
                          <button
                            key={`${action.type}-${action.id}`}
                            onClick={() => toggleExpected(action.id)}
                            className={`w-full px-3 py-2 text-right transition-colors
                              ${isSelected
                                ? 'bg-purple-50 border-r-2 border-purple-500 hover:bg-purple-100'
                                : 'bg-white hover:bg-purple-50/60'}`}
                          >
                            <div className="flex justify-between items-center gap-2">
                              {/* Checkbox indicator */}
                              <span className={`flex-shrink-0 ${isSelected ? 'text-purple-500' : 'text-gray-300'}`}>
                                {isSelected
                                  ? <CheckSquare className="w-3.5 h-3.5" />
                                  : <Square className="w-3.5 h-3.5" />}
                              </span>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-400">{fmtDate(action.action_date)}</span>
                                <span className={`inline-flex items-center px-1.5 rounded text-xs font-medium ${
                                  action.type === 'recurring'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {action.type === 'recurring' ? 'קבועה' : 'ידועה'}
                                </span>
                              </div>
                              <span className={`text-sm font-semibold ${action.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {fmtAmt(action.amount)} ₪
                              </span>
                            </div>
                            <p className="text-sm text-gray-900 font-medium truncate mt-0.5">{action.description}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {action.bank_accounts?.nickname || 'חשבון לא ידוע'}
                              {action.accounting_categories && (
                                <span className="mr-1 text-gray-400">· {action.accounting_categories.name}</span>
                              )}
                              {action.offset_codes && (
                                <span className="mr-1 text-blue-500">· {action.offset_codes.name}</span>
                              )}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </>
      )}
    </div>
  );
}
